// The seam: stem + 1px weld tick + ending in the accent colour. Load-bearing (DESIGN.md).
// Segmentation is data. When it is missing, unconfident or inconsistent, the full form is shown
// with no seam. Never guess a split.
// Inside a <SyllableScope> with "Tavut tekstissä" on, the form also shows its syllables; the tick
// stands alone where a syllable boundary falls on the seam (syllables/Syllables.tsx).
import { syllablePieces, syllableSplit, useInlineSyllables } from '../syllables/Syllables';

export interface SeamParts {
  stem: string;
  ending: string;
}

/**
 * The split to render, or null for "whole form, no seam".
 * Requires: confident !== false, both parts present, a non-empty stem, and stem + ending === form.
 * An empty ending (zero suffix) has nothing to colour, so it renders whole too.
 */
export function seamParts(form: string, stem?: string | null, ending?: string | null, confident?: boolean): SeamParts | null {
  if (confident === false) return null;
  if (stem == null || ending == null) return null;
  if (!stem || !ending) return null;
  if ((stem + ending).normalize('NFC') !== form.normalize('NFC')) return null;
  return { stem, ending };
}

export interface SeamProps {
  form: string;
  stem?: string | null;
  ending?: string | null;
  /** The paradigm's confidence flag. false → never a seam. */
  confident?: boolean;
  /** Ending in ink instead of accent (a card before its reveal). */
  quiet?: boolean;
  /** Underline the ending only (letters, example sentences). */
  underline?: boolean;
  /** A prefix and its word (nā|kostōba: the Pääteopas): the first part in the accent colour, the rest in ink. */
  prefix?: boolean;
  className?: string;
}

/** Renders inline inside an <Hv> (it does not set the HV face itself). */
export function Seam({ form, stem, ending, confident, quiet, underline, prefix, className }: SeamProps) {
  const parts = seamParts(form, stem, ending, confident);
  const syl = useInlineSyllables();
  const word = parts ? parts.stem + parts.ending : form;
  const split = syl ? syllableSplit(word) : null;
  const cls = ['seam', parts ? null : 'is-whole', quiet ? 'is-quiet' : null, underline ? 'is-underline' : null, className].filter(Boolean).join(' ');
  if (!parts) {
    return (
      <span className={cls} data-seam="none">
        {split ? syllablePieces(word, split, 0, word.length) : form}
      </span>
    );
  }
  return (
    <span className={cls} data-seam="split">
      <span className={prefix ? 'seam-en' : 'seam-st'}>{split ? syllablePieces(word, split, 0, parts.stem.length) : parts.stem}</span>
      <span className="seam-wt" aria-hidden="true" />
      <span className={prefix ? 'seam-st' : 'seam-en'}>
        {split ? syllablePieces(word, split, parts.stem.length, word.length, parts.stem.length) : parts.ending}
      </span>
    </span>
  );
}
